/**
 * Configure SSO Step 4 — IdP platform assist + inference.
 */
import type { VysorIdpOption } from './vysorCatalog';
import type { WizardQuickOption } from './vysorWizardEngine';
import {
  buildAgentContextHeader,
  isUnsureInput,
  resolveReferenceText,
  shouldDelegateToAgent,
  shouldTryReferenceInference,
  DYNAMIC_AI_REPLY_RULES,
} from './vysorWizardAssistCommon';
import { fetchUrlContentForVysor } from './vysorMetadata';

export type IdpPlatformInference = {
  platformId: string;
  label: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
};

export type IdpPlatformStepOutcome =
  | { kind: 'pass' }
  | { kind: 'inferred'; platformId: string; label: string; summary: string; referenceNote: string }
  | { kind: 'assist'; message: string; options: WizardQuickOption[] }
  | { kind: 'agent'; message: string; attachmentText?: string }
  | { kind: 'invalid'; hint: string };

const IDP_KEYWORDS: Record<string, string[]> = {
  okta: ['okta', 'okta.com'],
  azure: ['azure', 'entra', 'microsoft entra', 'azure ad', 'aad'],
  google: ['google', 'google workspace', 'g suite', 'gsuite'],
  generic_saml: ['generic saml', 'saml metadata', 'federation metadata'],
  generic_oidc: ['generic oidc', 'openid', 'oauth', 'oidc discovery'],
};

/** Real enterprise IdPs only (excludes generic fallbacks). */
export function realConfiguredIdps(idps: VysorIdpOption[]): VysorIdpOption[] {
  return idps.filter(i => i.id !== 'generic_saml' && i.id !== 'generic_oidc');
}

/**
 * Auto-detect IdP from tenant Settings — app owners should not be asked Okta vs Azure.
 */
export function pickDefaultIdpPlatform(
  idps: VysorIdpOption[],
  protocol?: string | null,
): string {
  const real = realConfiguredIdps(idps);
  if (real.length === 1) return real[0].id;
  const prefer =
    protocol === 'OIDC' ? ['azure', 'okta', 'google'] : ['okta', 'azure', 'google'];
  for (const id of prefer) {
    if (real.some(r => r.id === id)) return id;
  }
  if (real[0]) return real[0].id;
  return protocol === 'OIDC' ? 'generic_oidc' : 'generic_saml';
}

export function idpPlatformLabel(idps: VysorIdpOption[], platformId: string): string {
  return idps.find(i => i.id === platformId)?.label || platformId;
}

export function buildIdpAssistOptions(configuredIdps: VysorIdpOption[]): WizardQuickOption[] {
  const opts = configuredIdps.map(idp => ({
    id: `idp:${idp.id}`,
    label: idp.label,
    value: idp.id,
  }));
  opts.push({ id: 'wz_cancel', label: 'Cancel', value: '__cancel__' });
  return opts;
}

export function inferIdpPlatformFromText(
  text: string,
  configuredIdps: VysorIdpOption[],
  ssoType?: string,
): IdpPlatformInference | null {
  const lower = text.toLowerCase();
  const scores: Record<string, number> = {};
  for (const idp of configuredIdps) {
    scores[idp.id] = 0;
    const keys = IDP_KEYWORDS[idp.id] || [idp.label.toLowerCase(), idp.id];
    for (const kw of keys) {
      if (lower.includes(kw)) scores[idp.id] += kw.length > 4 ? 2 : 1;
    }
  }

  const ranked = configuredIdps
    .map(idp => ({ idp, score: scores[idp.id] || 0 }))
    .filter(r => r.score > 0)
    .sort((a, b) => b.score - a.score);

  if (!ranked.length) {
    if (ssoType === 'OIDC' && configuredIdps.some(i => i.id === 'generic_oidc')) {
      return {
        platformId: 'generic_oidc',
        label: 'Generic OIDC',
        confidence: 'low',
        reason: 'OIDC flow with no specific IdP named — Generic OIDC is a safe default.',
      };
    }
    if (ssoType === 'SAML' && configuredIdps.some(i => i.id === 'generic_saml')) {
      return {
        platformId: 'generic_saml',
        label: 'Generic SAML',
        confidence: 'low',
        reason: 'SAML flow with no specific IdP named — Generic SAML is a safe default.',
      };
    }
    return null;
  }

  const best = ranked[0];
  const confidence = best.score >= 4 ? 'high' : best.score >= 2 ? 'medium' : 'low';
  return {
    platformId: best.idp.id,
    label: best.idp.label,
    confidence,
    reason: `Reference mentions **${best.idp.label}**.`,
  };
}

export function buildIdpAgentContext(
  collected: Record<string, string>,
  configuredIdps: VysorIdpOption[],
  userMessage: string,
  attachmentText?: string,
): string {
  const idpNames = configuredIdps.map(i => i.label).join(', ');
  const lines = [
    'User is on Configure SSO — IdP platform step (override only).',
    `Application: ${collected.application_name || 'unknown'} (ID: ${collected.application_id || 'pending'})`,
    `Environment: ${collected.instance_name || 'unknown'} (ID: ${collected.instance_id || 'pending'})`,
    `SSO protocol: ${collected.sso_type || 'unknown'}`,
    `Available IdP platforms: ${idpNames}`,
    'Enterprise IdP is normally auto-detected from Settings — do not ask Okta vs Azure unless overriding.',
    '',
    DYNAMIC_AI_REPLY_RULES,
    'Recommend one of the available platforms when helpful. If confident, call create_sso_request with platform, sso_type, applicationId, instanceId.',
    '',
    `User: ${userMessage}`,
  ];
  const header = buildAgentContextHeader('Configure SSO — IdP platform step', lines);
  if (attachmentText?.trim()) {
    return `${header}\n[Reference material]\n${attachmentText.slice(0, 6000)}`;
  }
  return header;
}

export async function resolveIdpPlatformStepInput(
  rawAnswer: string,
  optionId: string | undefined,
  optionValue: string | undefined,
  collected: Record<string, string>,
  configuredIdps: VysorIdpOption[],
  assistActive: boolean,
): Promise<IdpPlatformStepOutcome> {
  const trimmed = rawAnswer.trim();
  const chipId = optionValue ?? '';

  if (configuredIdps.some(i => i.id === chipId)) {
    return { kind: 'pass' };
  }

  if (isUnsureInput(trimmed) || trimmed.toLowerCase() === 'not sure') {
    return {
      kind: 'agent',
      message: buildIdpAgentContext(
        collected,
        configuredIdps,
        trimmed || 'I am not sure which identity provider to use.',
      ),
    };
  }

  if (shouldTryReferenceInference(trimmed)) {
    try {
      const { text } = await resolveReferenceText(trimmed, async url => {
        const fetched = await fetchUrlContentForVysor(url);
        return { text: fetched.text };
      });
      const inferred = inferIdpPlatformFromText(text, configuredIdps, collected.sso_type);
      if (inferred && inferred.confidence !== 'low') {
        return {
          kind: 'inferred',
          platformId: inferred.platformId,
          label: inferred.label,
          summary: `Based on your reference, use **${inferred.label}** — ${inferred.reason}`,
          referenceNote: trimmed.slice(0, 2000),
        };
      }
    } catch {
      // fall through
    }
  }

  const inline = inferIdpPlatformFromText(trimmed, configuredIdps, collected.sso_type);
  if (inline && inline.confidence !== 'low' && configuredIdps.some(i => i.id === inline.platformId)) {
    return {
      kind: 'inferred',
      platformId: inline.platformId,
      label: inline.label,
      summary: `Sounds like **${inline.label}** — ${inline.reason}`,
      referenceNote: trimmed.slice(0, 500),
    };
  }

  if (shouldDelegateToAgent(trimmed, assistActive)) {
    let attachmentText: string | undefined;
    if (shouldTryReferenceInference(trimmed)) {
      try {
        const { text } = await resolveReferenceText(trimmed, async url => {
          const fetched = await fetchUrlContentForVysor(url);
          return { text: fetched.text };
        });
        attachmentText = text.slice(0, 8000);
      } catch {
        attachmentText = undefined;
      }
    }
    return {
      kind: 'agent',
      message: buildIdpAgentContext(collected, configuredIdps, trimmed, attachmentText),
      attachmentText,
    };
  }

  if (trimmed) {
    return { kind: 'pass' };
  }

  return {
    kind: 'invalid',
    hint:
      'Pick an IdP from the list, type its name, say **Not sure** to share references, or ask me to review your setup docs.',
  };
}
